import { Button } from "Components/Button";
import { useState, useEffect } from "react";
import { Box } from "styles/globalComponents";
import { useParams, useHistory } from "react-router";
import { createToast } from "Utils/toastFunc";
import {
  SavePreBudgetData,
  completePreBudget,
  saveCartChangesUnitAI,
  cancelPreBudgetUnit,
} from "Requests/Partners/PreBudgets";
import LoadingPurple from "Assets/Images/loading-purple.gif";
import { FormikProvider, useFormik } from "formik";
import { formatPhoneNumberIntl } from "react-phone-number-input";
import { useItemsContext } from "../../hooks/useItemsContext";
import { InfoDetails, PersonInfo, Values, personData } from "./InfoDetails";
import { CartSummary } from "./CartDetails/CartSummary";

interface CartDetailsProps {
  blockInteraction: boolean;
  setPageBlocked: (status: boolean) => void;
  patientData?: any;
  iaResponse: any;
}

type PersonInfoChanges = PersonInfo & {
  ddd: string;
  ddi: string;
};

export const CartDetails = ({
  blockInteraction,
  setPageBlocked,
  patientData,
  iaResponse,
}: CartDetailsProps) => {
  const [tab, setTab] = useState<"cart" | "info">("cart");
  const [loading, setLoading] = useState(false);

  const { items, clearCart, getTrackHistory, getNewTrackHistory } =
    useItemsContext();

  const { id } = useParams<{ id: string }>();
  const history = useHistory();

  // Estado do formulário movido para o componente pai
  const [formInitialized, setFormInitialized] = useState(false);
  const [formValues, setFormValues] = useState<Values>({
    name: "",
    document: "",
    phone: "",
    doctor: "",
    crm: "",
    mothers_full_name: "",
  });

  // Estado para controlar o modo de edição forçado quando há erros de validação
  const [forceEditMode, setForceEditMode] = useState(false);

  // Listener para redirecionar automaticamente para a aba dados quando há erros de validação
  useEffect(() => {
    const handleRedirectToInfo = () => {
      setTab("info");
    };

    window.addEventListener("redirectToInfoTab", handleRedirectToInfo);

    return () => {
      window.removeEventListener("redirectToInfoTab", handleRedirectToInfo);
    };
  }, []);

  // Inicializar formulário apenas uma vez quando os dados da API estiverem disponíveis
  useEffect(() => {
    if (patientData && !formInitialized) {
      const { ddd, ddi, phone } = patientData;

      let customDdi = "55";

      if (ddi) {
        customDdi = ddi.replace(/\D/g, "");
      }

      const initialValues: Values = {
        name: patientData.full_name || "",
        document: patientData.document || "",
        phone: `+${customDdi} ${ddd} ${phone}` || "",
        doctor: patientData.doctor || "",
        crm: patientData.crm || "",
        mothers_full_name: patientData.mothers_full_name || "",
      };
      setFormValues(initialValues);
      setFormInitialized(true);
    }
  }, [patientData, formInitialized]);

  // Atualizar formik apenas uma vez quando o formulário for inicializado
  useEffect(() => {
    if (formInitialized && !formik.values.name) {
      formik.setValues(formValues);
    }
  }, [formInitialized]); // Remover dependências que causam loop

  // Atualizar formulário quando dados da IA estiverem disponíveis
  useEffect(() => {
    if (iaResponse && formInitialized && formik.values.name) {
      const updates: Partial<Values> = {};

      // Preencher dados do médico se disponíveis na IA e não preenchidos pelo usuário
      if (
        iaResponse.consolidated_analysis?.ai_analysis?.requestingDoctor &&
        !formik.values.doctor
      ) {
        updates.doctor =
          iaResponse.consolidated_analysis.ai_analysis.requestingDoctor;
      }

      if (
        iaResponse.consolidated_analysis?.ai_analysis?.doctorCRM &&
        !formik.values.crm
      ) {
        updates.crm = iaResponse.consolidated_analysis.ai_analysis.doctorCRM;
      }

      if (Object.keys(updates).length > 0) {
        const newFormValues = { ...formik.values, ...updates };
        formik.setValues(newFormValues);

        // Log APENAS após o preenchimento automático
      }
    }
  }, [iaResponse, formInitialized]); // Remover formik.values.name da dependência

  // Função para apenas salvar alterações
  const handleSaveChanges = async (values?: PersonInfoChanges) => {
    if (!id || items.length === 0) {
      createToast("warning", "Não há alterações para salvar.");
      return;
    }

    // Se não há dados do formulário, redirecionar para aba dados
    if (!values || typeof values !== "object" || !("full_name" in values)) {
      setTab("info");
      return;
    }

    try {
      setPageBlocked(true);
      setLoading(true);

      // Preparar dados do carrinho
      const cartData = items.map((item) => ({
        product_id: item.product_id,
        amount: item.amount,
      }));

      // Preparar dados do paciente (usar valores do form se disponíveis, senão usar dados da API)
      const patientDataToSave = values || {
        full_name: patientData?.full_name || "Não informado",
        email: patientData?.email || "Não informado",
        phone: patientData?.phone || "Não informado",
        doctor: patientData?.doctor || "Não informado",
        crm: patientData?.crm || "",
        document: patientData?.document || "",
        mothers_full_name: patientData?.mothers_full_name || "",
      };

      // Preparar dados de tracking
      const trackData = getTrackHistory();
      const newTrackData = getNewTrackHistory();

      // Usar novo formato se disponível, senão usar formato antigo
      const formattedTrackData =
        newTrackData ||
        trackData.map((trackItem) => ({
          produto_original: {
            produto_original_nome:
              trackItem.produto_original.produto_original_nome,
            produto_original_match_nome:
              trackItem.produto_original.produto_original_match_nome,
            produto_original_match_id:
              trackItem.produto_original.produto_original_match_id,
          },
          items_corretos: trackItem.items_corretos || [],
        }));

      const saveData: SavePreBudgetData = {
        cart: cartData,
        patient: {
          ...patientDataToSave,
        },
        track: formattedTrackData,
      };

      // Salvar alterações (carrinho + dados paciente + track)
      await saveCartChangesUnitAI(Number(id), saveData);

      createToast("success", "Alterações salvas com sucesso!");
    } catch (error: any) {
      const errorMessage = error?.response?.data?.errors[0];

      if (errorMessage) {
        createToast("error", errorMessage);
        return;
      }
      createToast("error", "Erro ao salvar alterações. Tente novamente.");
    } finally {
      setPageBlocked(false);
      setLoading(false);
    }
  };

  // Função para salvar alterações E converter em orçamento
  const handleSubmit = async (values?: PersonInfo) => {
    if (!id || items.length === 0) {
      createToast("warning", "Não há alterações para salvar.");
      return;
    }

    // Se não há dados do formulário, redirecionar para aba dados
    if (!values || typeof values !== "object" || !("full_name" in values)) {
      setTab("info");
      return;
    }

    try {
      setPageBlocked(true);
      setLoading(true);

      // Preparar dados do carrinho
      const cartData = items.map((item) => ({
        product_id: item.product_id,
        amount: item.amount,
      }));

      const [ddi, ddd, n1, n2] = formatPhoneNumberIntl(values.phone).split(" ");

      // Preparar dados do paciente (usar valores do form se disponíveis, senão usar dados da API)

      const formPatientValues = {
        ...values,
        ddd,
        ddi: ddi.replace("+", ""),
        phone: `${n1}${n2}`,
      };

      const patientDataToSave = values
        ? formPatientValues
        : {
            full_name: patientData?.full_name || "Não informado",
            email: patientData?.email || "Não informado",
            phone: patientData?.phone || "Não informado",
            doctor: patientData?.doctor || "Não informado",
            crm: patientData?.crm || "",
            document: patientData?.document || "",
            mothers_full_name: patientData?.mothers_full_name || "",
            ddd: patientData?.ddd || "",
            ddi: patientData?.ddi || "",
          };

      // Preparar dados de tracking
      const trackData = getTrackHistory();
      const newTrackData = getNewTrackHistory();

      // Usar novo formato se disponível, senão usar formato antigo
      const formattedTrackData =
        newTrackData ||
        trackData.map((trackItem) => ({
          produto_original: {
            produto_original_nome:
              trackItem.produto_original.produto_original_nome,
            produto_original_match_nome:
              trackItem.produto_original.produto_original_match_nome,
            produto_original_match_id:
              trackItem.produto_original.produto_original_match_id,
          },
          items_corretos: trackItem.items_corretos || [],
        }));

      const saveData: SavePreBudgetData = {
        cart: cartData,
        patient: patientDataToSave,
        track: formattedTrackData,
      };

      // 1️⃣ Salvar alterações (carrinho + dados paciente + track)
      await saveCartChangesUnitAI(Number(id), saveData);

      // 2️⃣ Converter em orçamento
      const response = await completePreBudget(Number(id));

      if (response.status === 202) {
        createToast(
          "success",
          "Orçamento criado com sucesso. Os itens estão sendo processados em segundo plano."
        );
        history.push(`/listagem-pre-orcamento/domiciliar`);
        return;
      }

      const { id: budgetId } = response.data;

      createToast("success", "Orçamento criado com sucesso!");

      // Redirecionar para a página de orçamentos
      history.push(`/ficha-de-atendimento-parceiro/${budgetId}/domiciliar`);
    } catch (error: any) {
      console.error("Erro ao converter em orçamento:", error);
      createToast("error", "Erro ao converter em orçamento. Tente novamente.");
    } finally {
      setPageBlocked(false);
      setLoading(false);
    }
  };

  // Função de submit específica para o Formik - APENAS para salvar alterações
  const handleFormSubmit = (values: Values) => {
    // Converter Values para PersonInfo

    const [ddi, ddd, n1, n2] = formatPhoneNumberIntl(values.phone).split(" ");

    const patient = {
      full_name: values.name,
      email: patientData?.email || "",
      doctor: values.doctor || "",
      crm: values.crm || "",
      document: values.document || "",
      mothers_full_name: values.mothers_full_name || "",
      ddd,
      ddi: ddi.replace("+", ""),
      phone: `${n1}${n2}`,
    };

    // Se chegou até aqui, não há erros de validação - resetar modo forçado
    setForceEditMode(false);

    // Usar handleSaveChanges para apenas salvar (não converter)
    handleSaveChanges(patient);
  };

  // Hook do Formik criado após a declaração das funções
  const formik = useFormik({
    initialValues: formValues,
    onSubmit: handleFormSubmit,
    enableReinitialize: false,
    validationSchema: personData,
    validateOnMount: false,
    validateOnChange: true,
    validateOnBlur: true,
  });

  const handleCancelPreBudget = async () => {
    try {
      setPageBlocked(true);
      setLoading(true);

      await cancelPreBudgetUnit(Number(id));

      createToast("success", "Pré-orçamento cancelado com sucesso!");
      history.push("/listagem-pre-orcamento/domiciliar");
    } catch (error: any) {
      createToast("error", "Não foi possível cancelar o pré-orçamento");
    } finally {
      setPageBlocked(false);
      setLoading(false);
    }
  };

  const handleClearCart = () => {
    clearCart(); // Isso já reseta o track também (implementado no Provider)
    createToast("success", "Carrinho limpo com sucesso!");
  };

  const onValidateSubmit = async () => {
    // Validar o formulário antes de tentar salvar
    const validationResult = await formik.validateForm();

    // Verificar se há erros de validação
    if (Object.keys(validationResult).length > 0) {
      // Há erros de validação - ativar modo de edição forçado e mudar para aba dados
      setForceEditMode(true);
      setTab("info");

      const touchedFields: any = {};
      Object.keys(formik.errors).forEach((fieldName) => {
        if (formik.errors[fieldName as keyof typeof formik.errors]) {
          touchedFields[fieldName] = true;
        }
      });
      formik.setTouched(touchedFields);

      // Mostrar toast informativo sobre os erros
      createToast(
        "warning",
        "Por favor, corrija os erros no formulário antes de continuar."
      );

      return;
    }

    formik.submitForm();
  };

  return (
    <>
      <div
        className={`cart-information-resume ${
          blockInteraction ? "blocked" : ""
        }`}
      >
        <div className="tabs">
          <Button
            classButton={tab === "cart" ? "active" : ""}
            onClick={() => setTab("cart")}
            disabled={blockInteraction}
          >
            Carrinho
          </Button>
          <Button
            classButton={tab === "info" ? "active" : ""}
            onClick={() => setTab("info")}
            disabled={blockInteraction}
          >
            Dados
          </Button>
        </div>

        <div className="cart-details-container">
          {tab === "cart" && <CartSummary />}

          {tab === "info" && (
            <FormikProvider value={formik}>
              <InfoDetails
                blockUserInteraction={blockInteraction}
                forceEditMode={forceEditMode}
                onEditModeChange={setForceEditMode}
                iaData={{
                  requestingDoctor:
                    iaResponse?.consolidated_analysis?.ai_analysis
                      ?.requestingDoctor,
                  doctorCRM:
                    iaResponse?.consolidated_analysis?.ai_analysis?.doctorCRM,
                }}
              />
            </FormikProvider>
          )}
        </div>

        <Box className="cart-buttons">
          <Button
            onClick={onValidateSubmit}
            disabled={blockInteraction}
            classButton={loading ? "loading-button" : ""}
          >
            {loading ? (
              <img src={LoadingPurple} alt="" className="gif" />
            ) : (
              "Salvar Alterações"
            )}
          </Button>

          <Button
            onClick={async () => {
              // Validar o formulário antes de tentar converter
              const validationResult = await formik.validateForm();

              // Verificar se há erros de validação
              if (Object.keys(validationResult).length > 0) {
                // Há erros de validação - ativar modo de edição forçado e mudar para aba dados
                setForceEditMode(true);
                setTab("info");

                const touchedFields: any = {};
                Object.keys(formik.errors).forEach((fieldName) => {
                  if (formik.errors[fieldName as keyof typeof formik.errors]) {
                    touchedFields[fieldName] = true;
                  }
                });
                formik.setTouched(touchedFields);

                // Mostrar toast informativo sobre os erros
                createToast(
                  "warning",
                  "Por favor, corrija os erros no formulário antes de continuar."
                );
              } else {
                // Se não há erros, chamar handleSubmit diretamente para salvar + converter
                const patient: PersonInfo = {
                  full_name: formik.values.name,
                  email: patientData?.email || "",
                  phone: formik.values.phone,
                  doctor: formik.values.doctor || "",
                  crm: formik.values.crm || "",
                  document: formik.values.document || "",
                  mothers_full_name: formik.values.mothers_full_name || "",
                };
                handleSubmit(patient);
              }
            }}
            disabled={blockInteraction}
            classButton={loading ? "loading-button" : ""}
          >
            {loading ? (
              <img src={LoadingPurple} alt="" className="gif" />
            ) : (
              "Converter em orçamento"
            )}
          </Button>

          <Button
            outlined
            onClick={handleCancelPreBudget}
            disabled={blockInteraction}
            classButton={loading ? "loading-button" : ""}
          >
            {loading ? (
              <img src={LoadingPurple} alt="" className="gif" />
            ) : (
              "Cancelar pré-orçamento"
            )}
          </Button>
          <Button
            outlined
            onClick={handleClearCart}
            disabled={blockInteraction || items.length === 0}
          >
            Limpar carrinho
          </Button>
        </Box>
      </div>

      <style>{`
      .cart-buttons .loading-button {
        position: relative;
        padding: 0;
        width: auto;
        pointer-events: none;
        opacity: 0.6;
      }

      .cart-buttons button .gif {
        position: absolute;
        top: 0px;
        bottom: 0px;
        right: 0;
        left: 0;
        margin: auto;
        display: block;
        width: 36px;
        height: 36px;
      }
    `}</style>
    </>
  );
};
